import React from 'react';
import PropTypes from 'prop-types';
import {Parser} from 'html-to-react';
import Pikaday from 'pikaday';
import {DateTimeFormat} from 'common/proxies/enum-proxy';
import './index.scss';

export default class ReportForm extends React.Component {
  static propTypes = {
    handleChange: PropTypes.func,
    handleUpdate: PropTypes.func,
    hiddenFields: PropTypes.array,
    report: PropTypes.string,
  };

  static defaultProps = {
    handleChange: () => {},
    handleUpdate: () => {},
    hiddenFields: [],
    report: '',
  };

  constructor() {
    super();
    this.calendars = [];
    this.handleChange = this.handleChange.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.report !== prevProps.report) {
      window.initPopovers();
      this.initCalendars();
      this.handleUpdate();
    }
  }

  getCleanedHtml(html) {
    if (html) {
      return (
        html
          // Empty popover titles render as "data-original-title",
          // so strip out any empty popover titles.
          .replace('data-original-title=""', '')
      );
    }
    return html;
  }

  getFieldset(html, hiddenFields = []) {
    if (html) {
      // We only need the fieldset for present purposes,
      // not the whole form:
      const fieldset = html.match(/<fieldset((.|[\n\r])*)<\/fieldset>/).shift();

      // Filter out fields (like text inputs) that don't
      // belong in analytics apps. There are a many ways
      // to do this of course, but for present purposes,
      // jQuery is most convenient:
      return $(fieldset)
        .children()
        .filter((index, row) => {
          for (const hiddenField of hiddenFields) {
            if ($(row).find(`input[name='${hiddenField}']`).length) {
              return false;
            }
          }
          return true;
        })
        .map((index, row) => row.outerHTML)
        .toArray()
        .join('');
    }
    return '';
  }

  getFormRows(fieldset, changeHandler) {
    if (fieldset) {
      // Parse HTML string to React element tree:
      const parser = new Parser();
      const formRows = parser.parse(fieldset);

      // Walk React element tree and add the
      // change handler to all form inputs.
      const addHandler = (elements) => {
        if (!Array.isArray(elements)) {
          return elements;
        }
        return elements.map((element) => {
          const props = element.props || {};

          // If the element is a string literal, don't try
          // to clone it into a new React element, because
          // it will be full of undefined fields, and
          // React will explode on render. But do clone
          // all of the other elements.
          return typeof element === 'string'
            ? element
            : React.cloneElement(element, {
                ...props,
                children: props.children ? addHandler(props.children) : null,
                onChange: props.name ? changeHandler : null,
              });
        });
      };
      return addHandler(formRows);
    }
    return [];
  }

  getSerializedForm(form) {
    // Empty HTML form values serialize, inconveniently,
    // as value=value. I'm not sure if this is the fault
    // of React or html-to-react, but in any event, let's
    // restore empty values so that form requests validate.
    return $(form)
      .serializeArray()
      .filter((datum) => datum.value !== 'value')
      .map((datum) => `${datum.name}=${datum.value}`)
      .join('&');
  }

  getStyle(loading, formRows) {
    return {
      pointerEvents: loading ? 'none' : 'auto',
      visibility: formRows && formRows.length ? 'visible' : 'hidden',
    };
  }

  initCalendars() {
    this.calendars.forEach((calendar) => calendar.destroy());
    this.calendars = [];
    $.each($(this._form).find('input[data-calendar]'), (index, el) => {
      this.calendars.push(
        new Pikaday({
          field: el,
          format: DateTimeFormat.DATE,
          onClose: () => {
            this.handleChange({
              target: el,
            });
          },
        }),
      );
    });
  }

  render() {
    const cleanedHtml = this.getCleanedHtml(this.props.report.html);
    const fieldset = this.getFieldset(cleanedHtml, this.props.hiddenFields);
    const formRows = this.getFormRows(fieldset, this.handleChange);
    const style = this.getStyle(this.props.report.loading, formRows);
    return (
      <form
        className={'ReportForm'}
        ref={(form) => (this._form = form)}
        style={style}>
        {formRows}
      </form>
    );
  }

  handleChange(event) {
    const params = this.getSerializedForm(this._form);
    const changedField = event.target.getAttribute('name');
    this.props.handleChange(`${params}&changed_field=${changedField}`);
  }

  handleUpdate() {
    this.props.handleUpdate(this.getSerializedForm(this._form));
  }
}
